import React from 'react';
import { Typography, Paper, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import NotificationsIcon from '@mui/icons-material/Notifications';
import LanguageIcon from '@mui/icons-material/Language';

const ProfileSettings = () => {
  const settingsOptions = [
    {
      icon: <PersonIcon />,
      title: "Personal Information",
      description: "Update your name, job title, and other personal details to keep your profile current."
    },
    {
      icon: <EmailIcon />,
      title: "Contact Information",
      description: "Manage your email address, phone number, and other contact details for account-related communications."
    },
    {
      icon: <VpnKeyIcon />,
      title: "Password and Security",
      description: "Change your password, enable two-factor authentication, and manage other security settings."
    },
    {
      icon: <NotificationsIcon />,
      title: "Notification Preferences",
      description: "Customize how and when you receive notifications from the AI Talker system."
    },
    {
      icon: <LanguageIcon />,
      title: "Language and Localization",
      description: "Set your preferred language and regional settings for the dashboard and notifications."
    }
  ];

  return (
    <Paper elevation={0}>
      <Typography variant="h4" gutterBottom>Profile Settings</Typography>
      <Typography paragraph>
        Your profile settings allow you to personalize your AI Talker experience and manage your account details. 
        Keeping your profile up-to-date ensures smooth communication and enhances your interaction with the platform.
      </Typography>

      <List>
        {settingsOptions.map((option, index) => (
          <ListItem key={index}>
            <ListItemIcon>
              {option.icon}
            </ListItemIcon>
            <ListItemText 
              primary={option.title}
              secondary={option.description}
            />
          </ListItem>
        ))}
      </List>

      <Typography variant="h6" style={{ marginTop: '20px' }}>Managing Your Profile:</Typography>
      <Typography paragraph>
        1. <strong>Access Profile Settings:</strong> Navigate to the Settings section in the main dashboard and select "Profile Settings".
      </Typography>
      <Typography paragraph>
        2. <strong>Review Current Information:</strong> Take a moment to review your existing profile information for accuracy.
      </Typography>
      <Typography paragraph>
        3. <strong>Make Updates:</strong> Click on the edit icon next to each section to make changes to your profile information.
      </Typography>
      <Typography paragraph>
        4. <strong>Save Changes:</strong> After making updates, be sure to save your changes by clicking the "Save" or "Update" button.
      </Typography>
      <Typography paragraph>
        5. <strong>Security Check:</strong> When making significant changes (like updating your email or password), you may be required to verify your identity.
      </Typography>

      <Typography variant="h6" style={{ marginTop: '20px' }}>Best Practices:</Typography>
      <Typography paragraph>
        - Regularly review and update your profile information to ensure it remains accurate.
      </Typography>
      <Typography paragraph>
        - Use a strong, unique password and enable two-factor authentication for enhanced security.
      </Typography>
      <Typography paragraph>
        - Keep your contact information current to receive important updates and notifications.
      </Typography>
      <Typography paragraph>
        - Customize your notification preferences to stay informed without being overwhelmed.
      </Typography>

      <Typography paragraph style={{ marginTop: '20px' }}>
        Remember, keeping your profile up-to-date not only ensures smooth operation of your AI Talker account 
        but also helps us provide you with the best possible support and service. If you encounter any issues 
        while updating your profile or have questions about specific settings, our support team is always 
        available to assist you.
      </Typography>
    </Paper>
  );
};

export default ProfileSettings;
